import axios from 'axios';
import queryString from 'query-string';
import i18n from 'i18next';
import { addDays, format, parse } from 'date-fns';
import { reduce, snakeCase } from 'lodash';
import { API_URL } from '../config';

export const getPayUrl = async (orderId, type, isSave, isAutomaticRecurringPayment) => {
  const queryParams = {
    order_id: orderId,
    lang: i18n.language,
    payment_method: type,
    is_save: isSave,
    is_automatic_recurring_payment: +isAutomaticRecurringPayment,
  };

  const URL = `${API_URL}/payment/link?${queryString.stringify(queryParams)}`;
  return axios.get(URL).then((response) => response.data);
};

export const makeBindingTransaction = async (orderId, cardHolderID) => {
  const URL = `${API_URL}/payment/binding-transaction`;
  return axios
    .post(
      URL,
      {
        orderId,
        userCode: cardHolderID,
      },
      { headers: { 'x-bypass-humps': 'true' } },
    )
    .then((response) => response.data);
};

export const deactivateCardBinding = (cardHolderID) => {
  const URL = `${API_URL}/payment/deactivate-bindings`;
  return axios.post(
    URL,
    { holderId: cardHolderID },
    { headers: { 'x-bypass-humps': 'true' } },
  );
};

export const fetchPaymentReceipt = async (data) => {
  const URL = `${API_URL}/payment/receipt`;
  return axios(URL, {
    method: 'POST',
    data,
  }).then((response) => response.data);
};

export async function fetchDraftPaymentFromCache(id) {
  const URL = `${API_URL}/cache/${id}`;
  return axios(URL, {
    method: 'GET',
  }).then((response) => response.data);
}

export const prepareTableParams = (params, isExport) => {
  const {
    order = 'desc',
    sortBy = 'created_at',
  } = params;

  if (isExport) {
    return { 'sort[]': `${snakeCase(sortBy)},${order}` };
  }

  const {
    page = 1,
    limit: perPage = 10,
  } = params;

  return {
    page,
    perPage,
    'sort[]': `${snakeCase(sortBy)},${order}`,
  };
};

export const prepareParams = (params, filters, isExport = false) => {
  const dateRange = [];

  const filterParams = reduce(
    filters,
    (result, val, key) => {
      if (key === '(ge)') {
        (
          dateRange[val.searchParamKey] || (dateRange[val.searchParamKey] = [])
        ).push(
          `${key}${format(
            parse(val.value, 'dd-MM-yyyy', new Date()),
            'yyyy-MM-dd',
          )}`,
        );
      } else if (key === '(le)') {
        (
          dateRange[val.searchParamKey] || (dateRange[val.searchParamKey] = [])
        ).push(
          `${'(lt)'}${format(
            addDays(parse(val.value, 'dd-MM-yyyy', new Date()), 1),
            'yyyy-MM-dd',
          )}`,
        );
      } else if (key === 'paymentMethodId') {
        result[snakeCase(key)] = val; // eslint-disable-line no-param-reassign
      } else {
        result[snakeCase(key)] = `%${val}%`; // eslint-disable-line no-param-reassign
      }
      return result;
    },
    {},
  );

  const tblParams = prepareTableParams(params, isExport);

  return {
    ...dateRange,
    ...filterParams,
    ...tblParams,
  };
};

export const getRecurringPaymentsList = async (params, filters) => {
  const paramsToStringify = prepareParams(params, filters);
  const URL = `${API_URL}/recurring-payment?${queryString.stringify(paramsToStringify)}`;
  return axios.get(URL).then((response) => response.data);
};

export const exportRecurringPayments = async (params, filters) => {
  const exportSortParams = { ...params, order: 'desc', sortBy: 'created_at' };
  const paramsToStringify = prepareParams(exportSortParams, filters, true);
  const URL = `${API_URL}/recurring-payments-export?${queryString.stringify(paramsToStringify)}&fileFormat=${params.fileFormat}`;
  return axios.get(URL).then((response) => response.data);
};

export const fetchContract = async (reqData) => {
  const URL = `${API_URL}/policy/validation`;
  return axios.post(URL, reqData).then(({ data }) => ({
    ...data.result,
    policyNumber: reqData.policyNumber,
  }));
};

export const fetchContractSubscription = async (reqData) => {
  const URL = `${API_URL}/contract-subscription/search/${reqData.policyNumber}`;
  return axios.get(URL).then(({ data }) => ({
    ...data.result,
    attachedCard: data.attachedCard,
    policyNumber: reqData.policyNumber,
  }));
};

export const fetchSavedCards = async () => {
  const URL = `${API_URL}/payment/cards`;
  return axios.get(URL).then((res) => Object.values(res.data));
};

export const fetchAttachedCard = async (bindingId) => {
  const URL = `${API_URL}/contract-subscription/attached-card/${bindingId}`;
  return axios(URL, {
    method: 'GET',
  }).then((response) => response.data);
};

export const postFreePayment = async (orderId) => {
  const URL = `${API_URL}/payment/freepaid`;
  return axios
    .post(
      URL,
      {
        orderId,
      },
      { headers: { 'x-bypass-humps': 'true' } },
    )
    .then((response) => response.data);
};

export const makeCardAsDefault = (cardHolderID) => {
  const URL = `${API_URL}/payment/make-card-default`;
  return axios.post(
    URL,
    { holderId: cardHolderID },
    { headers: { 'x-bypass-humps': 'true' } },
  );
};

export const getAddNewCardUrl = async (subscriptionType, orderId) => {
  const queryParams = {
    lang: i18n.language,
    type: subscriptionType,
    orderId,
  };
  const URL = `${API_URL}/payment/attach-new-card?${queryString.stringify(queryParams)}`;
  return axios.get(URL).then((response) => response.data);
};

export const getAutomaticRecurringPaymentScheduleByToken = async (type, token, order) => {
  const URL = `${API_URL}/automatic-recurring-payment-by-token`;
  return axios.get(URL, { params: { type, token, order } }).then((response) => response.data);
};

export const getFailedRecurringPayments = async (params) => {
  const URL = `${API_URL}/failed-automatic-recurring-payments`;
  return axios.get(`${URL}?${queryString.stringify(params)}`).then((response) => response.data);
};

export const getRecurringPaymentAttempts = async (params) => {
  const URL = `${API_URL}/automatic-recurring-payment-attempts`;
  return axios.get(`${URL}?${queryString.stringify(params)}`).then((response) => response.data);
};

export const retryRecurringPayment = async (id) => {
  const URL = `${API_URL}/retry-automatic-recurring-payment`;
  return axios.post(URL, { id }).then((response) => response.data);
};

export const recurringPaymentFutureNotifications = async (params) => {
  const URL = `${API_URL}/automatic-recurring-payment-future-notifications`;
  return axios.get(`${URL}?${queryString.stringify(params)}`).then((response) => response.data);
};
